<template>
  <ContainerBasic
    classes="max-w-screen-lg mx-auto print:mt-lg page-break-inside print:pl-16 print:pr-16"
    :class="isHeader ? '!mt-0 md:!mt-0' : 'mt-md md:mt-xl'"
  >
    <HeadlineDynamic
      v-if="props.blockData.headline"
      :has-decoration="true"
      :headline="props.blockData.headline"
      :level="props.blockData.headlineLevel"
    />
    <ClientOnly>
      <ImageCarousel
        :show="state.showCarousel"
        :images="blockData.images"
        :active-index="state.carouselIndex"
        @close="state.showCarousel = false"
      />
      <section
        v-if="blockData.images"
        class="section px-md md:px-0 mt-md md:mt-xl"
      >
        <div class="flex flex-wrap items-center -m-1 md:-m-2">
          <div
            v-for="(image, index) in blockData.images"
            :key="image.id"
            :class="'flex flex-wrap ' + numColumns"
            style="page-break-inside: avoid"
          >
            <div class="w-full p-xs md:p-sm">
              <img
                style="page-break-inside: avoid"
                :src="image.url"
                :alt="image.altText"
                :title="image.title"
                loading="lazy"
                class="self-auto mx-auto cursor-pointer object-contain hover:scale-[1.02]"
                :class="maxImageHeight"
                data-bs-toggle="modal"
                :data-bs-target="'#cmsImageGallery'"
                @click="openImageCarousel(index)"
              />
            </div>
          </div>
        </div>
      </section>
    </ClientOnly>
    <div v-if="isHeader" class="bg-activesection-base w-full h-[36px] mt-sm" />
  </ContainerBasic>
</template>

<script setup lang="ts">
import ImageCarousel from '@/components/components/core/image-carousel/image-carousel.vue';
import ContainerBasic from '@/components/components/container-basic.vue';
import HeadlineDynamic from '@/components/components/core/headline-dynamic/vdv/headline-dynamic.vue';
import { mq_breakpointIsMobile } from '@/injectionSymbols';
import type { ImageGalleryData } from '../blockType';

const props = defineProps({
  blockData: {
    type: Object as PropType<ImageGalleryData>,
    required: true,
    default: {} as ImageGalleryData,
  },
  isHeader: {
    type: Boolean,
    required: true,
    default: () => {
      return false;
    },
  },
});

const isMobile = inject(mq_breakpointIsMobile);

const maxImageHeight = computed(() => {
  if (isMobile.value === true) {
    return 'max-h-24 print:max-h-full';
  }
  if (props.blockData.images.length < 5) {
    return 'max-h-80 print:max-h-full';
  }
  return 'max-h-36 print:max-h-full';
});

const numColumns = computed(() => {
  if (props.blockData.images.length < 5 || isMobile.value === true) {
    return 'w-1/2 print:!w-1/4';
  }
  return 'w-1/4 print:!w-1/4';
});

type ImageGalleryDefaultState = {
  carouselIndex: number;
  showCarousel: boolean;
};

const state: ImageGalleryDefaultState = reactive({
  carouselIndex: 0,
  showCarousel: false,
});

onUnmounted(() => {
  state.carouselIndex = 0;
  state.showCarousel = false;
});

const openImageCarousel = (imageIndex: number) => {
  state.carouselIndex = imageIndex;
  state.showCarousel = true;
};
</script>

<style scoped lang="postcss">
.page-break-inside {
  page-break-inside: avoid;
}
</style>
